// ===========================================
// Hybrid Booking API Functions
// ===========================================
// API calls for hybrid lesson booking endpoints

import { apiClient } from '../services/api';
import { Lesson, HybridPattern } from './lessons.api';
import { Student } from './users.api';
import { parseUTCDate } from '../utils/dateUtils';
import type { LockableTermLike } from '../utils/termLock';
import type { Instrument, ProgramLevel } from './admin.api';

// ===========================================
// TYPES
// ===========================================

/**
 * The picker lists every role may read (`/calendar/*`, #272), typed as the server sends them.
 * A term is exactly what the term-lock mirror needs; the admin `Term`, `Instrument` and
 * `ProgramLevel` are assignable to these, so a page can take either source.
 */
export type TermOption = LockableTermLike;
export type InstrumentOption = Pick<Instrument, 'id' | 'name' | 'color' | 'sortOrder' | 'isActive'>;
export type ProgramLevelOption = Pick<ProgramLevel, 'id' | 'name' | 'displayName' | 'color' | 'sortOrder' | 'isActive'>;

export interface TimeSlot {
  date: string;
  // Calendar-day key (YYYY-MM-DD), tz-stable. Prefer this for both display
  // and as the wire identifier when submitting bookings/reschedules.
  dateKey: string;
  startTime: string;
  endTime: string;
  // The term week this slot physically lands in (N for the clinic week, N+1 for overflow).
  weekNumber: number;
  // The 1:1 clinic week this slot is FOR. Equals weekNumber for a normal slot;
  // for an overflow slot it's weekNumber - 1. (Optional for back-compat with any
  // older cached payloads; defaults to weekNumber when absent.)
  clinicWeekNumber?: number;
  // True when this slot is an overflow slot (the clinic booked into week N+1).
  isOverflow?: boolean;
  isAvailable: boolean;
}

export interface HybridBooking {
  id: string;
  lessonId: string;
  studentId: string;
  parentId: string;
  weekNumber: number;
  /** The 1:1 clinic week this booking is FOR. Null for legacy rows (= weekNumber). */
  clinicWeekNumber: number | null;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  bookedAt: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Every /hybrid-bookings/* route returns the raw Prisma rows (`bookingInclude`),
  // so relation keys are PascalCase. There are deliberately NO camelCase aliases:
  // a type declaring both casings let reads of `booking.lesson` compile while
  // the key never arrived (#204 — blank class/child names on the parent pages).
  Lesson: Lesson;
  Student: Student;
  /** Id and name only: never the family's contact details (#326). */
  Parent: {
    id: string;
    User: { firstName: string; lastName: string };
  };
  isAdminPreBooking: boolean;
  adminNotes: string | null;
  adminCreatedById: string | null;
  /** Present on the two admin lists only (pre-bookings, student bookings). */
  AdminCreatedBy?: { id: string; firstName: string; lastName: string } | null;
  /** Per-booking teacher override (null = the lesson's regular teacher). */
  overrideTeacherId?: string | null;
  OverrideTeacher?: {
    id: string;
    User: { id: string; firstName: string; lastName: string };
  } | null;
}

export interface AdminPreBookingInput {
  lessonId: string;
  studentId: string;
  familyId: string;
  /** Scheduled term week (the actual date's week). */
  weekNumber: number;
  /** The 1:1 clinic week this booking is FOR (may differ from weekNumber). */
  clinicWeekNumber: number;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  adminNotes?: string;
  /**
   * Optional per-booking teacher override (Section 2b). Omit = use the student's
   * regular (lesson) teacher.
   */
  overrideTeacherId?: string;
}

/** A teacher the admin may assign to a 1:1 clinic (Section 2b). */
export interface EligibleClinicTeacher {
  id: string;
  name: string;
  /** True for the lesson's primary teacher (the student's regular teacher). */
  isDefault: boolean;
}

export interface AdminPreBookingFilters {
  lessonId?: string;
  weekNumber?: number;
  /** Filter by the 1:1 clinic week (clinicWeekNumber ?? weekNumber). */
  clinicWeekNumber?: number;
  studentId?: string;
}

export interface BookingStats {
  totalStudents: number;
  bookedCount: number;
  unbookedCount: number;
  completionRate: number;
  pendingBookings: number;
  confirmedBookings: number;
}

export interface CreateBookingInput {
  lessonId: string;
  studentId: string;
  weekNumber: number;
  /**
   * The 1:1 clinic week this booking is FOR. Send for an overflow booking
   * (clinic week N booked into week N+1, so clinicWeekNumber = weekNumber - 1).
   * Omit for a normal booking (the server infers clinicWeekNumber = weekNumber).
   */
  clinicWeekNumber?: number;
  scheduledDate: string;
  startTime: string;
  endTime: string;
}

export interface RescheduleBookingInput {
  scheduledDate: string;
  startTime: string;
  endTime: string;
}

/** Admin per-booking room override (Change Location on a 1:1 clinic). */
export interface ChangeBookingRoomInput {
  roomId: string;
  overrideConflicts?: boolean;
  reason?: string;
  notifyParents?: boolean;
}

/** Admin per-booking teacher override (Change Teacher on a 1:1 clinic, 2026-09-06). */
export interface ChangeBookingTeacherInput {
  /** null (or the lesson's own teacher) = clear the override. */
  teacherId: string | null;
  overrideConflicts?: boolean;
  reason?: string;
  notifyParents?: boolean;
}

export interface BookingFilters {
  lessonId?: string;
  status?: string;
  weekNumber?: number;
}

export interface LessonBookingFilters {
  weekNumber?: number;
  /** Filter by the 1:1 clinic week (clinicWeekNumber ?? weekNumber). */
  clinicWeekNumber?: number;
  status?: string;
}

export interface HybridBookingStatus {
  totalHybridLessons: number;
  openCount: number;
  closedCount: number;
  allOpen: boolean;
  /** How many hybrid lessons have overflow (week N+1) bookings open. */
  overflowOpenCount: number;
  /** True when every hybrid lesson has overflow open. */
  overflowAllOpen: boolean;
  termName: string | null;
}

export interface BookingOpenStatsWeekEntry {
  bookingId: string;
  weekNumber: number;
  /** The clinic week this booking is FOR. Differs from weekNumber for overflow (held in N+1). */
  clinicWeekNumber: number;
  startTime: string;
  endTime: string;
  scheduledDate: string;
  /** Per-booking teacher override (null = the lesson's regular teacher). */
  overrideTeacherId: string | null;
  /** The teacher the clinic resolves to, for display. */
  teacherName: string;
}

export interface BookingOpenStatsRecipient {
  parentId: string;
  parentUserId: string;
  parentName: string;
  parentEmail: string;
  parentPhone: string | null;
  studentId: string;
  studentName: string;
  booked: boolean;
  bookedAt: string | null;
  weekNumber: number | null;
  weekBookings: BookingOpenStatsWeekEntry[];
  /**
   * The student's EFFECTIVE start week (max of class and enrolment start; 1 when neither
   * is set). Optional: a stale backend omits it, and a missing value means week 1 — no
   * clinic week is ever treated as "not applicable" without it.
   */
  startWeek?: number;
}

export interface BookingOpenStats {
  lessonName: string;
  emailsSentCount: number;
  bookingsMadeCount: number;
  recipients: BookingOpenStatsRecipient[];
}

export interface TermBookingOpenStatsLesson {
  lessonId: string;
  lessonName: string;
  emailsSentCount: number;
  bookingsMadeCount: number;
  individualWeeks: number[];
  recipients: BookingOpenStatsRecipient[];
  teacherName: string | null;
  teacherColor: string | null;
  instrumentName: string | null;
  instrumentColor: string | null;
  roomName: string | null;
  locationName: string | null;
  dayOfWeek: number | null;
  /** Sprint B: what `nudgeableClinicWeeks` needs — see `useHybridBooking.ts`. */
  termStartDate: string;
  termEndDate: string;
  openClinicWeeks: number[];
  startTime: string | null;
  endTime: string | null;
}

export interface TermBookingOpenStats {
  totalEmailsSent: number;
  totalBookingsMade: number;
  lessons: TermBookingOpenStatsLesson[];
}

export interface BulkToggleResult {
  updatedCount: number;
  lessonIds: string[];
}

/** Outcome of an admin nudge — counts only, never addresses. */
export interface SendRemindersResult {
  /** Students in the class with no booking for that clinic week. */
  unbooked: number;
  /** Families emailed by this press. */
  nudged: number;
  /** Already nudged for this week (by the sweep or an earlier press) — nothing sent. */
  alreadyNudged: number;
  /** No contact email on file, or every contact opted out / preference off. */
  noContactEmail: number;
  /** Claimed but a send failed — held on the ledger, will not be retried. */
  undelivered: number;
  emailsSent: number;
}

/** Client-side ceiling for the inline nudge request (see `sendReminders`). */
export const SEND_REMINDERS_TIMEOUT_MS = 180_000;

/** One row of GET /lessons/:id/unbooked: names and ids only (#326). */
export interface UnbookedStudent {
  student: { id: string; firstName: string; lastName: string };
  parent: {
    id: string;
    User: { firstName: string; lastName: string };
  };
}

// Calendar event type for react-big-calendar
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  // Backend-supplied calendar day (YYYY-MM-DD, derived from UTC components of
  // the source row) and wall-clock times. When present, the API client uses
  // these to construct local-tz `start`/`end` so the event displays on the
  // intended day regardless of server/client timezone.
  dateKey?: string;
  startTime?: string;
  endTime?: string;
  allDay: boolean;
  resourceId?: string; // For resource view (teacher/room columns)
  resource: {
    type: 'INDIVIDUAL' | 'GROUP' | 'BAND' | 'HYBRID_GROUP' | 'HYBRID_INDIVIDUAL' | 'HYBRID_PLACEHOLDER' | 'MEET_AND_GREET' | 'MAKEUP' | 'EXTRA_LESSON';
    lessonId?: string;
    lessonName?: string;
    teacherId?: string;
    teacherName?: string;
    teacherColor?: string;
    // When this occurrence is covered by a replacement, the original primary
    // teacher so the modal can show both. teacherName/teacherColor already hold
    // the resolved (replacement) teacher for this week.
    isReplaced?: boolean;
    originalTeacherName?: string;
    originalTeacherColor?: string;
    lessonTypeName?: string;
    lessonTypeColor?: string;
    instrumentId?: string;
    instrumentName?: string;
    instrumentColor?: string;
    programLevelName?: string;
    programLevelColor?: string;
    streamColor?: string;
    roomId?: string;
    roomName?: string;
    locationName?: string;
    enrolledCount?: number;
    maxStudents?: number;
    isBooking?: boolean;
    studentName?: string;
    /** On MEET_AND_GREET events this is the Meet & Greet's id. */
    bookingId?: string;
    /** MEET_AND_GREET events (#255): the meeting happened and no notes are in. */
    notesMissing?: boolean;
    /** Set on MAKEUP events — the MakeupSlot row id */
    slotId?: string;
    /** Set on MAKEUP events — the Term the slot belongs to */
    termId?: string;
    /**
     * EXTRA_LESSON make-ups (holiday make-ups, 2026-09-28). The ADMIN/TEACHER feed
     * carries the made-up class and the staff note; the PARENT feed carries only
     * `familyNote`, and only when the admin shared it.
     */
    makeupForLessonName?: string;
    staffNote?: string;
    shareNoteWithFamily?: boolean;
    familyNote?: string;
    weekNumber?: number;
    bookingsOpen?: boolean;
    isRescheduled?: boolean;
    isCancelled?: boolean;
    isAbsenteeReleased?: boolean;
    originalDate?: string;
    termStartDate?: string;
    termEndDate?: string;
    /**
     * Mid-term start week (Phase 4, D5). PARENT feed only, on LESSON events (a clinic
     * booking event carries none). Optional: every reader treats a missing value as
     * "no restriction".
     * `enrolmentStartWeek` = the child's own `LessonEnrollment.startWeek`,
     * `classStartWeek` = `Lesson.startWeek`, `lessonDayOfWeek` = `Lesson.dayOfWeek`.
     */
    enrolmentStartWeek?: number | null;
    classStartWeek?: number | null;
    lessonDayOfWeek?: number | null;
  };
}

export interface CalendarEventsFilters {
  termId?: string;
  teacherId?: string;
  locationId?: string;
  roomId?: string;
  instrumentId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface PaginatedCalendarEvents {
  events: CalendarEvent[];
  pagination: PaginationInfo;
}

// ===========================================
// TEACHER TASKS — HYBRID INDIVIDUAL-SESSION ROW
// ===========================================

/**
 * Row returned by GET /hybrid-bookings/teacher-tasks. Each row is one active
 * HybridBooking for a GROUP_INDIVIDUAL hybrid lesson in the requested calendar
 * week. The Teacher Tasks page merges these into the lesson list alongside
 * regular lesson rows, ordered by (dayOfWeek, startTime).
 *
 * `scheduledDate` is the ISO string from the wire — use `parseUTCDate` to read
 * its UTC-anchored calendar day for rendering.
 */
export interface HybridSessionTaskRow {
  bookingId: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'NO_SHOW';
  weekNumber: number;
  scheduledDate: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  lessonId: string;
  lessonName: string;
  teacherId: string;
  Instructor: {
    id: string;
    color: string | null;
    User: { id: string; firstName: string; lastName: string };
  };
  Room: {
    id: string;
    name: string;
    Location: { id: string; name: string };
  };
  LessonType: { id: string; name: string; color: string | null };
  Student: {
    id: string;
    firstName: string;
    lastName: string;
    avatar: string | null;
    /** `@db.Date` ISO string — feeds the birthday cake on the roster pill. */
    birthDate: string | null;
    AgeGroup: { id: string; name: string } | null;
  };
  // Discriminator so consumers can branch on row type.
  isHybridIndividualSession: true;
}

export interface TeacherTaskSessionsParams {
  // ISO date string accepted by the backend's permissive `dateSchema`
  // (typically YYYY-MM-DD; full ISO works too). Both ends inclusive.
  weekStart: string;
  weekEnd: string;
  teacherId?: string;
}

// ===========================================
// HYBRID BOOKING API
// ===========================================

export const hybridBookingApi = {
  // ===========================================
  // PARENT HYBRID LESSON ACCESS
  // ===========================================

  // Get hybrid lessons the parent's children are enrolled in
  getMyHybridLessons: (): Promise<Lesson[]> =>
    apiClient
      .get<{ status: string; data: Lesson[] }>('/hybrid-bookings/my-lessons')
      .then((res) => res.data),

  // ===========================================
  // PARENT BOOKING OPERATIONS
  // ===========================================

  // Get available time slots for a specific week
  getAvailableSlots: (lessonId: string, weekNumber: number): Promise<TimeSlot[]> =>
    apiClient
      .get<{ status: string; data: TimeSlot[] }>('/hybrid-bookings/available-slots', {
        params: { lessonId, weekNumber },
      })
      .then((res) => res.data),

  // Create a new booking
  create: (data: CreateBookingInput): Promise<HybridBooking> =>
    apiClient
      .post<{ status: string; data: HybridBooking }>('/hybrid-bookings', data)
      .then((res) => res.data),

  // Get parent's bookings
  getMyBookings: (filters?: BookingFilters): Promise<HybridBooking[]> =>
    apiClient
      .get<{ status: string; data: HybridBooking[] }>('/hybrid-bookings/my-bookings', {
        params: filters,
      })
      .then((res) => res.data),

  // Get a single booking by ID
  getById: (id: string): Promise<HybridBooking> =>
    apiClient
      .get<{ status: string; data: HybridBooking }>(`/hybrid-bookings/${id}`)
      .then((res) => res.data),

  // Reschedule a booking
  reschedule: (id: string, data: RescheduleBookingInput): Promise<HybridBooking> =>
    apiClient
      .patch<{ status: string; data: HybridBooking }>(`/hybrid-bookings/${id}`, data)
      .then((res) => res.data),

  // Slots across ALL remaining term weeks for an OPEN-WEEK booking
  // (admin freed this clinic from its fixed week)
  getOpenWeekSlots: (bookingId: string): Promise<TimeSlot[]> =>
    apiClient
      .get<{ status: string; data: TimeSlot[] }>(`/hybrid-bookings/${bookingId}/open-week-slots`)
      .then((res) => res.data),

  // Admin: open/close a clinic week of a class to any term week (pattern-level;
  // covers existing bookings AND families yet to book)
  setClinicWeekOpen: (
    lessonId: string,
    clinicWeek: number,
    open: boolean
  ): Promise<{ openClinicWeeks: number[] }> =>
    apiClient
      .patch<{ status: string; data: { openClinicWeeks: number[] } }>(
        `/hybrid-bookings/lessons/${lessonId}/open-clinic-week`,
        { clinicWeek, open }
      )
      .then((res) => res.data),

  // Cancel a booking
  cancel: (id: string, reason?: string): Promise<void> =>
    apiClient
      .delete(`/hybrid-bookings/${id}`, { data: { reason } })
      .then(() => undefined),

  // ===========================================
  // ADMIN BULK BOOKING MANAGEMENT
  // ===========================================

  // Get hybrid booking status for current term
  getBookingStatus: (): Promise<HybridBookingStatus> =>
    apiClient
      .get<{ status: string; data: HybridBookingStatus }>('/hybrid-bookings/status')
      .then((res) => res.data),

  // Bulk toggle bookings for all hybrid lessons in current term
  bulkToggle: (open: boolean): Promise<BulkToggleResult> =>
    apiClient
      .patch<{ status: string; data: BulkToggleResult; message: string }>(
        '/hybrid-bookings/bulk-toggle',
        { open }
      )
      .then((res) => res.data),

  // Bulk toggle OVERFLOW bookings (week N+1) for all hybrid lessons in current term
  bulkToggleOverflow: (open: boolean): Promise<BulkToggleResult> =>
    apiClient
      .patch<{ status: string; data: BulkToggleResult; message: string }>(
        '/hybrid-bookings/bulk-toggle-overflow',
        { open }
      )
      .then((res) => res.data),

  // ===========================================
  // ADMIN HYBRID MANAGEMENT OPERATIONS
  // ===========================================

  // Open bookings for a lesson
  openBookings: (lessonId: string): Promise<HybridPattern> =>
    apiClient
      .patch<{ status: string; data: HybridPattern }>(
        `/hybrid-bookings/lessons/${lessonId}/open-bookings`
      )
      .then((res) => res.data),

  // Close bookings for a lesson
  closeBookings: (lessonId: string): Promise<HybridPattern> =>
    apiClient
      .patch<{ status: string; data: HybridPattern }>(
        `/hybrid-bookings/lessons/${lessonId}/close-bookings`
      )
      .then((res) => res.data),

  // Get all bookings for a lesson (admin/teacher view)
  getLessonBookings: (
    lessonId: string,
    filters?: LessonBookingFilters
  ): Promise<HybridBooking[]> =>
    apiClient
      .get<{ status: string; data: HybridBooking[] }>(
        `/hybrid-bookings/lessons/${lessonId}/bookings`,
        { params: filters }
      )
      .then((res) => res.data),

  // Get booking statistics for a lesson
  getBookingStats: (lessonId: string, weekNumber?: number): Promise<BookingStats> =>
    apiClient
      .get<{ status: string; data: BookingStats }>(
        `/hybrid-bookings/lessons/${lessonId}/stats`,
        { params: weekNumber ? { weekNumber } : {} }
      )
      .then((res) => res.data),

  // Get booking-open stats for a single lesson
  getBookingOpenStats: (lessonId: string): Promise<BookingOpenStats> =>
    apiClient
      .get<{ status: string; data: BookingOpenStats }>(
        `/hybrid-bookings/lessons/${lessonId}/booking-open-stats`
      )
      .then((res) => res.data),

  // Get aggregated booking-open stats across all open lessons in the current term
  getTermBookingOpenStats: (): Promise<TermBookingOpenStats> =>
    apiClient
      .get<{ status: string; data: TermBookingOpenStats }>(
        '/hybrid-bookings/booking-open-stats'
      )
      .then((res) => res.data),

  // Get hybrid individual sessions for the Teacher Tasks page.
  // Returns active HybridBooking rows for GROUP_INDIVIDUAL hybrid lessons whose
  // scheduledDate falls within the supplied calendar week.
  getTeacherTaskSessions: (params: TeacherTaskSessionsParams): Promise<HybridSessionTaskRow[]> =>
    apiClient
      .get<{ status: string; data: HybridSessionTaskRow[] }>(
        '/hybrid-bookings/teacher-tasks',
        { params }
      )
      .then((res) => res.data),

  // Get students who haven't booked for a specific week
  getUnbookedStudents: (lessonId: string, weekNumber: number): Promise<UnbookedStudent[]> =>
    apiClient
      .get<{ status: string; data: UnbookedStudent[] }>(
        `/hybrid-bookings/lessons/${lessonId}/unbooked`,
        { params: { weekNumber } }
      )
      .then((res) => res.data),

  // Admin "nudge now" (Sprint B): email every family in this class who has not
  // booked clinic week N, on the same send-once ledger as the daily sweep. A
  // second press for the same week reports `alreadyNudged` and sends nothing.
  // ⚠️ Counts only — the old stub returned parent email addresses.
  sendReminders: (lessonId: string, weekNumber: number): Promise<SendRemindersResult> =>
    apiClient
      .post<{ status: string; data: SendRemindersResult }>(
        `/hybrid-bookings/lessons/${lessonId}/send-reminders`,
        // ⚠️ `{}`, not `null`. The client's JSON Content-Type makes axios
        // stringify the body, and JSON.stringify(null) is the literal text
        // "null", which Express's strict parser rejects with
        // `Unexpected token 'n', "null" is not valid JSON` — seen on the first
        // real press on staging (2026-09-04). Tests mock the client boundary,
        // so only a real request could catch it.
        {},
        {
          params: { weekNumber },
          // The server sends inline and serially. Under a sustained Mailgun
          // rate limit one recipient can cost ~90 s, so a class can outlive
          // nginx's 60 s proxy timeout — the server still finishes, and the
          // ledger makes a re-press safe. This is the client-side ceiling
          // after which the hook tells the admin to re-check, not re-press.
          timeout: SEND_REMINDERS_TIMEOUT_MS,
        }
      )
      .then((res) => res.data),

  // Admin: get available slots bypassing bookingsOpen + 24h notice.
  // When teacherId is passed, slots are generated against that teacher's
  // availability instead of the lesson's primary (Section 2b override).
  getAdminAvailableSlots: (
    lessonId: string,
    weekNumber: number,
    bookingId?: string,
    teacherId?: string
  ): Promise<TimeSlot[]> =>
    apiClient
      .get<{ status: string; data: TimeSlot[] }>(
        `/hybrid-bookings/lessons/${lessonId}/admin-available-slots`,
        {
          params: {
            weekNumber,
            ...(bookingId && { bookingId }),
            ...(teacherId && { teacherId }),
          },
        }
      )
      .then((res) => res.data),

  // Admin: list teachers assignable to a 1:1 clinic for this lesson (Section 2b)
  getEligibleClinicTeachers: (lessonId: string): Promise<EligibleClinicTeacher[]> =>
    apiClient
      .get<{ status: string; data: EligibleClinicTeacher[] }>(
        `/hybrid-bookings/lessons/${lessonId}/eligible-teachers`
      )
      .then((res) => res.data),

  // Admin: list pre-bookings
  getAdminPreBookings: (filters?: AdminPreBookingFilters): Promise<HybridBooking[]> =>
    apiClient
      .get<{ status: string; data: HybridBooking[] }>('/hybrid-bookings/admin/pre-bookings', {
        params: filters,
      })
      .then((res) => res.data),

  // Admin: create a pre-booking
  createAdminPreBooking: (data: AdminPreBookingInput): Promise<HybridBooking> =>
    apiClient
      .post<{ status: string; data: HybridBooking }>('/hybrid-bookings/admin/pre-book', data)
      .then((res) => res.data),

  // Admin: cancel a pre-booking
  // Omitted `notifyParents` means SEND — the backend schema defaults it to true,
  // so a caller that doesn't pass it can never accidentally silence the family.
  cancelAdminPreBooking: (id: string, notifyParents?: boolean): Promise<void> =>
    apiClient
      .delete(`/hybrid-bookings/admin/pre-book/${id}`, { data: { notifyParents } })
      .then(() => undefined),

  // Admin: list all hybrid bookings for a student
  getAdminStudentBookings: (
    studentId: string,
    options?: { includeCancelled?: boolean }
  ): Promise<HybridBooking[]> =>
    apiClient
      .get<{ status: string; data: HybridBooking[] }>(
        `/hybrid-bookings/admin/students/${studentId}/bookings`,
        { params: options?.includeCancelled ? { includeCancelled: 'true' } : {} }
      )
      .then((res) => res.data),

  // Admin: reschedule any hybrid booking (bypasses parent ownership, bookingsOpen, 24h notice)
  adminRescheduleBooking: (
    id: string,
    data: RescheduleBookingInput
  ): Promise<HybridBooking> =>
    apiClient
      .patch<{ status: string; data: HybridBooking }>(
        `/hybrid-bookings/admin/bookings/${id}`,
        data
      )
      .then((res) => res.data),

  // Admin: override the ROOM of a single 1:1 clinic booking ("Change Location")
  adminChangeBookingRoom: (
    id: string,
    data: ChangeBookingRoomInput
  ): Promise<HybridBooking> =>
    apiClient
      .patch<{ status: string; data: HybridBooking }>(
        `/hybrid-bookings/admin/bookings/${id}/room`,
        data
      )
      .then((res) => res.data),

  // Admin: set/change/clear the TEACHER of a single 1:1 clinic booking ("Change Teacher")
  adminChangeBookingTeacher: (
    id: string,
    data: ChangeBookingTeacherInput
  ): Promise<HybridBooking> =>
    apiClient
      .patch<{ status: string; data: HybridBooking }>(
        `/hybrid-bookings/admin/bookings/${id}/teacher`,
        data
      )
      .then((res) => res.data),

  // Admin: cancel any hybrid booking (bypasses parent ownership)
  // Omitted `notifyParents` means SEND — the backend schema defaults it to true,
  // so a caller that doesn't pass it can never accidentally silence the family.
  adminCancelBooking: (id: string, reason?: string, notifyParents?: boolean): Promise<void> =>
    apiClient
      .delete(`/hybrid-bookings/admin/bookings/${id}`, { data: { reason, notifyParents } })
      .then(() => undefined),
};

// ===========================================
// CALENDAR API
// ===========================================

/**
 * Reconstruct local-tz `start`/`end` Dates from the backend-supplied `dateKey`
 * (`YYYY-MM-DD` in UTC) plus `startTime`/`endTime` (`HH:mm`). Falls back to parsing
 * the original `start`/`end` ISO strings when those fields are absent (older
 * payloads or third-party event types).
 *
 * The "fake-local" technique: the calendar day comes from `dateKey` and the wall-clock time
 * from `startTime`/`endTime`, both read back with LOCAL getters by every render site. Planting
 * local and reading local is the identity, so the event shows the school's intended day and
 * time for every viewer, in every timezone — never the viewer's re-localisation of an instant.
 */
function reconstructEventDates(event: CalendarEvent): CalendarEvent {
  if (event.dateKey && event.startTime && event.endTime) {
    const [y, m, d] = event.dateKey.split('-').map(Number);
    const [sh, sm] = event.startTime.split(':').map(Number);
    const [eh, em] = event.endTime.split(':').map(Number);
    // Build the LOCAL-anchored Date straight from the key's own parts.
    //
    // ⚠️ Do NOT route this through `new Date(Date.UTC(...))` and then read it back with LOCAL
    // getters. That was the shipped bug: `Date.UTC(2026,7,22)` read via `.getDate()` returns
    // **21** anywhere west of UTC, so every event rendered a day early — and because the drag
    // payload faithfully round-trips what is rendered, a US-based admin then SAVED the wrong
    // day. The `y/m/d` parts are already the calendar day; use them directly.
    return {
      ...event,
      start: new Date(y, m - 1, d, sh, sm, 0, 0),
      end: new Date(y, m - 1, d, eh, em, 0, 0),
    };
  }
  return {
    ...event,
    start: new Date(event.start),
    end: new Date(event.end),
  };
}

export const calendarApi = {
  /**
   * Get calendar events (admin/teacher) with pagination
   * @param filters - Optional filters including page and limit
   * @returns Paginated calendar events
   */
  getEvents: (filters?: CalendarEventsFilters): Promise<PaginatedCalendarEvents> =>
    apiClient
      .get<{ status: string; data: CalendarEvent[]; pagination: PaginationInfo }>('/calendar/events', {
        params: filters,
      })
      .then((res) => ({
        events: res.data.map(reconstructEventDates),
        pagination: res.pagination,
      })),

  /**
   * Get all calendar events (admin/teacher) - fetches all pages
   * Use for calendar views that need all events at once
   * @param filters - Optional filters (excluding pagination)
   * @returns All calendar events
   */
  getAllEvents: async (filters?: Omit<CalendarEventsFilters, 'page' | 'limit'>): Promise<CalendarEvent[]> => {
    const allEvents: CalendarEvent[] = [];
    let page = 1;
    const limit = 500; // Max per page
    let hasMore = true;

    while (hasMore) {
      const result = await calendarApi.getEvents({ ...filters, page, limit });
      allEvents.push(...result.events);
      hasMore = result.pagination.hasMore;
      page++;
    }

    return allEvents;
  },

  /**
   * Get calendar events for parent's children with pagination
   */
  getMyEvents: (filters?: CalendarEventsFilters): Promise<PaginatedCalendarEvents> =>
    apiClient
      .get<{ status: string; data: CalendarEvent[]; pagination: PaginationInfo }>('/calendar/my-events', {
        params: filters,
      })
      .then((res) => ({
        events: res.data.map(reconstructEventDates),
        pagination: res.pagination,
      })),

  /**
   * Get all calendar events for parent - fetches all pages
   */
  getAllMyEvents: async (filters?: Omit<CalendarEventsFilters, 'page' | 'limit'>): Promise<CalendarEvent[]> => {
    const allEvents: CalendarEvent[] = [];
    let page = 1;
    const limit = 500;
    let hasMore = true;

    while (hasMore) {
      const result = await calendarApi.getMyEvents({ ...filters, page, limit });
      allEvents.push(...result.events);
      hasMore = result.pagination.hasMore;
      page++;
    }

    return allEvents;
  },

  /**
   * Terms for the school as picker options: every role may read it, students included (#272)
   */
  getTerms: (): Promise<TermOption[]> =>
    apiClient
      .get<{ status: string; data: TermOption[] }>('/calendar/terms')
      .then((res) => res.data),

  /**
   * Instruments for the school as picker options, inactive included (parents, teachers, admins)
   */
  getInstruments: (): Promise<InstrumentOption[]> =>
    apiClient
      .get<{ status: string; data: InstrumentOption[] }>('/calendar/instruments')
      .then((res) => res.data),

  /**
   * Active program levels for the school as picker options (parents, teachers, admins)
   */
  getProgramLevels: (): Promise<ProgramLevelOption[]> =>
    apiClient
      .get<{ status: string; data: ProgramLevelOption[] }>('/calendar/program-levels')
      .then((res) => res.data),
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Get booking status color for chip
 */
export const getBookingStatusColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'CONFIRMED':
      return 'success';
    case 'CANCELLED':
      return 'error';
    case 'COMPLETED':
      return 'primary';
    case 'NO_SHOW':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Get calendar event type color
 * Uses Music 'n Me brand colors (matches defaultLessonTypeColors in brandColors.ts):
 * - Primary Blue: #4580E4 (Individual)
 * - Mint: #96DAC9 (Group)
 * - Coral: #FFAE9E (Band)
 * - Yellow: #FFCE00 (Hybrid)
 * - Cream: #FCF6E6
 */
// Special event colors interface (matches CalendarDisplaySettingsModal)
export interface SpecialEventColors {
  hybridGroup?: string;
  hybridIndividual?: string;
  hybridPlaceholder?: string;
  meetAndGreet?: string;
}

export const getEventTypeColor = (type: string, specialEventColors?: SpecialEventColors): string => {
  switch (type) {
    case 'INDIVIDUAL':
      return '#4580E4'; // Primary blue
    case 'GROUP':
      return '#96DAC9'; // Mint
    case 'BAND':
      return '#FFAE9E'; // Coral (matches defaultLessonTypeColors)
    case 'HYBRID':
      return '#FFCE00'; // Yellow (matches defaultLessonTypeColors)
    case 'HYBRID_GROUP':
      return specialEventColors?.hybridGroup || '#96DAC9'; // Mint (same as group)
    case 'HYBRID_INDIVIDUAL':
      return specialEventColors?.hybridIndividual || '#FFAE9E'; // Coral
    case 'HYBRID_PLACEHOLDER':
      return specialEventColors?.hybridPlaceholder || '#E8DDD0'; // Muted cream
    case 'MEET_AND_GREET':
      return specialEventColors?.meetAndGreet || '#4580E4'; // Primary blue
    case 'EXTRA_LESSON':
      return '#FFCE00'; // Brand secondary yellow — distinct, attention-grabbing
    case 'MAKEUP':
      return '#96DAC9'; // Mint
    default:
      return '#4580E4';
  }
};

/**
 * Format time slot for display
 */
export const formatTimeSlot = (startTime: string, endTime: string): string => {
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

/**
 * Check if booking can be modified (24h notice)
 */
export const canModifyBooking = (scheduledDate: string, startTime: string): boolean => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const bookingTime = parseUTCDate(scheduledDate);
  bookingTime.setUTCHours(hours, minutes, 0, 0);

  const now = new Date();
  const hoursUntilBooking = (bookingTime.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursUntilBooking >= 24;
};

/**
 * Get hours until booking
 */
export const getHoursUntilBooking = (scheduledDate: string, startTime: string): number => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const bookingTime = parseUTCDate(scheduledDate);
  bookingTime.setUTCHours(hours, minutes, 0, 0);

  const now = new Date();
  return Math.max(0, (bookingTime.getTime() - now.getTime()) / (1000 * 60 * 60));
};
